import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useContact, useDeals, useDeleteContact, useDeleteActivity, useProjectRequests, useUpdateActivity, useTenantUsers } from '../hooks';
import { useAuth } from '../../auth/AuthContext';
import { usePermissions } from '../../auth/usePermissions';
import { contactsApi, activitiesApi } from '../api';
import { ContactFormModal } from './ContactFormModal';
import { ActivityFormModal } from '../activities/ActivityFormModal';
import { Modal } from '../../components/Modal';
import { CustomFieldsDisplay } from '../CustomFieldsDisplay';
import { EmailsTab } from './EmailsTab';
import { CalendarTab } from './CalendarTab';
import type { Contact, Activity, ActivityType, ActivityComment, CompanyProjectRequest, TenantUser } from '../types';
import { ENTITY_STATUS_LABELS } from '../types';
import { RichTextDisplay } from '../../components/RichTextEditor';
import styles from './ContactDetailPage.module.css';

type PageTab = 'details' | 'activities' | 'outlook';
type M365Tab = 'emails' | 'calendar';

const ACTIVITY_TYPE_LABELS: Record<ActivityType, string> = {
  NOTE: 'Notiz',
  CALL: 'Anruf',
  EMAIL: 'E-Mail',
  MEETING: 'Meeting',
  TASK: 'Aufgabe',
  FOLLOWUP: 'Follow-Up',
  APPOINTMENT: 'Termin',
  SOCIAL_MEDIA: 'Social Media',
  MESSENGER: 'Messenger',
  EVENT: 'Event – Teilnahme',
};

function activityIcon(type: ActivityType): React.ReactNode {
  const s = {
    width: 14,
    height: 14,
    stroke: 'currentColor',
    fill: 'none',
    strokeWidth: 1.5,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };
  switch (type) {
    case 'NOTE':
      return (
        <svg viewBox="0 0 16 16" {...s}>
          <path d="M12 1l3 3-9 9H3v-3z" />
        </svg>
      );
    case 'CALL':
      return (
        <svg viewBox="0 0 16 16" {...s}>
          <path d="M1 3a2 2 0 012-2h1.5l2 3.5-1.5 1a7.5 7.5 0 004 4l1-1.5L13.5 10H15a2 2 0 01-2 2h-1A10 10 0 011 4V3z" />
        </svg>
      );
    case 'EMAIL':
      return (
        <svg viewBox="0 0 16 16" {...s}>
          <rect x="1" y="3" width="14" height="10" rx="1" />
          <path d="M1 4l7 5 7-5" />
        </svg>
      );
    case 'MEETING':
      return (
        <svg viewBox="0 0 16 16" {...s}>
          <rect x="2" y="3" width="12" height="11" rx="1" />
          <path d="M5 1v3M11 1v3M2 7h12" />
        </svg>
      );
    case 'TASK':
      return (
        <svg viewBox="0 0 16 16" {...s}>
          <path d="M4 8l3 3 5-6" />
          <rect x="1" y="1" width="14" height="14" rx="2" />
        </svg>
      );
    default:
      return null;
  }
}

function contactDisplayName(c: Contact): string {
  if (c.type === 'ORGANIZATION') return c.companyName ?? '—';
  return [c.firstName, c.lastName].filter(Boolean).join(' ') || '—';
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

const currencyFormatter = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
});

/** Parst E-Mail/Meeting-Beschreibungen und trennt Meta-Header vom Body */
function parseActivityDescription(desc: string, type: string): { meta: Record<string, string>; body: string; isEmail: boolean } {
  if (!desc || (type !== 'EMAIL' && type !== 'MEETING')) return { meta: {}, body: desc, isEmail: false };

  const lines = desc.split('\n');
  const meta: Record<string, string> = {};
  let bodyStart = 0;
  const headerKeys = ['von:', 'an:', 'cc:', 'datum:', 'anhaenge:', 'betreff:', 'from:', 'to:', 'date:', 'subject:',
    'zeitraum:', 'organisator:', 'teilnehmer:', 'ort:', 'online-meeting:', 'teams-link:'];

  for (let i = 0; i < lines.length; i++) {
    const trimmed = lines[i].trim();
    const lower = trimmed.toLowerCase();
    if (lower === '--- inhalt ---' || lower === '--- content ---' || lower === '') {
      bodyStart = i + 1;
      if (lower.startsWith('---')) bodyStart = i + 1;
      if (Object.keys(meta).length > 0 && lower === '') break;
      continue;
    }
    const match = headerKeys.find((k) => lower.startsWith(k));
    if (match) {
      const key = match.replace(':', '').trim();
      meta[key] = trimmed.substring(match.length).trim();
      bodyStart = i + 1;
    } else if (Object.keys(meta).length > 0) {
      break;
    }
  }

  const body = lines.slice(bodyStart).join('\n').trim();
  return { meta, body, isEmail: Object.keys(meta).length > 0 };
}

/** Rendert eine Activity-Beschreibung — E-Mails mit bereinigtem Layout, Rest als pre-wrap */
function ActivityDescription({ description, type }: { description: string; type: string }) {
  const { meta, body, isEmail } = parseActivityDescription(description, type);

  if (!isEmail) {
    return <RichTextDisplay html={description} style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }} />;
  }

  return (
    <div style={{ fontSize: '0.8125rem' }}>
      {/* Kompakte Meta-Zeile */}
      {Object.keys(meta).length > 0 && (
        <div style={{
          display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1rem',
          padding: '0.5rem 0.75rem', background: 'var(--color-bg)',
          borderRadius: 'var(--radius-sm)', marginBottom: '0.5rem',
          fontSize: '0.75rem', color: 'var(--color-text-muted)',
        }}>
          {Object.entries(meta).map(([key, val]) => (
            <span key={key}>
              <span style={{ fontWeight: 600, color: 'var(--color-text-secondary)', textTransform: 'capitalize' }}>{key}: </span>
              {val}
            </span>
          ))}
        </div>
      )}
      {/* Bereinigter Body */}
      {body && (
        <div style={{
          padding: '0.5rem 0.75rem', background: 'var(--color-bg-card)',
          borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border)',
          color: 'var(--color-text-secondary)', lineHeight: 1.6,
          maxHeight: 300, overflowY: 'auto', whiteSpace: 'pre-wrap',
        }}>
          {body}
        </div>
      )}
    </div>
  );
}

// LinkedIn icon SVG
function LinkedInIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 16 16"
      fill="currentColor"
      style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }}
    >
      <path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854V1.146zm4.943 12.248V6.169H2.542v7.225h2.401zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248-.822 0-1.359.54-1.359 1.248 0 .694.521 1.248 1.327 1.248h.016zm4.908 8.212V9.359c0-.216.016-.432.08-.586.175-.431.573-.878 1.242-.878.877 0 1.228.67 1.228 1.652v3.847h2.4V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016a5.54 5.54 0 0 1 .016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225h2.4z" />
    </svg>
  );
}

function XingIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor"
      style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }}>
      <path d="M5.39 4.27H2.26L6.2 11.07 2 19.73h3.13l4.2-8.66zM17.3 0l-7.56 14.71L13.99 24h3.13l-4.26-9.29L20.43 0z" />
    </svg>
  );
}

const fieldIconStyle: React.CSSProperties = {
  width: 12, height: 12, flexShrink: 0, marginRight: 4,
  stroke: 'currentColor', fill: 'none', strokeWidth: 1.8,
  strokeLinecap: 'round', strokeLinejoin: 'round',
  verticalAlign: 'middle', display: 'inline',
};

function IconEmail() {
  return <svg viewBox="0 0 16 16" style={fieldIconStyle}><rect x="1" y="3" width="14" height="10" rx="1" /><path d="M1 4l7 5 7-5" /></svg>;
}
function IconPhone() {
  return <svg viewBox="0 0 16 16" style={fieldIconStyle}><path d="M1 3a2 2 0 012-2h1.5l2 3.5-1.5 1a7.5 7.5 0 004 4l1-1.5L13.5 10H15a2 2 0 01-2 2h-1A10 10 0 011 4V3z" /></svg>;
}
function IconGlobe() {
  return <svg viewBox="0 0 16 16" style={fieldIconStyle}><circle cx="8" cy="8" r="7" /><path d="M8 1c-2 3-2 11 0 14M1 8h14M2 5h12M2 11h12" /></svg>;
}
function IconPin() {
  return <svg viewBox="0 0 16 16" style={fieldIconStyle}><path d="M8 1a5 5 0 015 5c0 3.5-5 9-5 9S3 9.5 3 6a5 5 0 015-5z" /><circle cx="8" cy="6" r="1.5" /></svg>;
}

function CopyField({ value, children }: { value: string; children: React.ReactNode; id?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'default' }}
      title="Klicken zum Kopieren"
    >
      {children}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          navigator.clipboard.writeText(value).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
        style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px',
          color: copied ? 'var(--color-success)' : 'var(--color-text-muted)',
          opacity: 0.7, lineHeight: 1, fontSize: 11,
        }}
        title={copied ? 'Kopiert!' : 'Kopieren'}
      >
        {copied ? '✓' : (
          <svg width="12" height="12" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <rect x="5" y="5" width="9" height="9" rx="1" />
            <path d="M11 5V3a1 1 0 00-1-1H3a1 1 0 00-1 1v7a1 1 0 001 1h2" />
          </svg>
        )}
      </button>
    </span>
  );
}

const PREFERRED_CONTACT_LABELS: Record<string, string> = {
  EMAIL: 'E-Mail',
  PHONE: 'Telefon',
  MOBILE: 'Mobil',
};

const DECISION_LEVEL_LABELS: Record<string, string> = {
  C_LEVEL: 'C-Level',
  DIRECTOR: 'Direktor/in',
  MANAGER: 'Manager/in',
  OPERATIONAL: 'Operativ',
  OTHER: 'Sonstige',
};

function ActivityDetailPopup({
  activity,
  onClose,
  canDelete,
  onDelete,
  currentUser,
  projectRequests,
}: {
  activity: Activity;
  onClose: () => void;
  canDelete: boolean;
  onDelete: () => void;
  currentUser: { firstName?: string; lastName?: string; email?: string } | null;
  projectRequests: CompanyProjectRequest[];
}) {
  const qc = useQueryClient();
  const updateActivity = useUpdateActivity();
  const { data: fullData } = useQuery({
    queryKey: ['crm', 'activity', activity.id],
    queryFn: () => activitiesApi.getById(activity.id),
    staleTime: 0,
  });
  const comments: ActivityComment[] = (fullData?.data as Activity & { comments?: ActivityComment[] })?.comments ?? [];

  const [newComment, setNewComment] = useState('');
  const [saving, setSaving] = useState(false);

  const userName = currentUser
    ? [currentUser.firstName, currentUser.lastName].filter(Boolean).join(' ') || currentUser.email || ''
    : '';

  const [commentError, setCommentError] = useState('');

  async function handleAddComment() {
    if (!newComment.trim() || saving) return;
    setSaving(true);
    setCommentError('');
    try {
      await activitiesApi.addComment(activity.id, newComment.trim(), userName);
      setNewComment('');
      await qc.invalidateQueries({ queryKey: ['crm', 'activity', activity.id] });
    } catch (err: any) {
      setCommentError(err?.response?.data?.error?.message ?? err?.message ?? 'Fehler beim Speichern');
    }
    setSaving(false);
  }

  async function handleDeleteComment(commentId: string) {
    await activitiesApi.deleteComment(activity.id, commentId);
    qc.invalidateQueries({ queryKey: ['crm', 'activity', activity.id] });
  }

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'var(--color-bg-card)', borderRadius: 'var(--radius-md)',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)', width: '100%', maxWidth: 560,
          maxHeight: '80vh', overflow: 'auto', padding: '1.5rem',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
          <div>
            <h2 style={{ fontSize: '1.0625rem', fontWeight: 600, margin: 0 }}>
              {activity.subject}
            </h2>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.375rem', flexWrap: 'wrap' }}>
              <span style={{
                fontSize: '0.6875rem', fontWeight: 600,
                padding: '0.125rem 0.5rem', borderRadius: '999px',
                background: activity.type === 'EMAIL' ? '#dbeafe' : activity.type === 'CALL' ? '#dcfce7' : activity.type === 'MEETING' ? '#fef3c7' : '#f3f4f6',
                color: activity.type === 'EMAIL' ? 'var(--color-info-text)' : activity.type === 'CALL' ? 'var(--color-success-text)' : activity.type === 'MEETING' ? 'var(--color-warning-text)' : '#6b7280',
              }}>
                {ACTIVITY_TYPE_LABELS[activity.type]}
              </span>
              {activity.projectRequest && (
                <span style={{
                  fontSize: '0.6875rem', fontWeight: 600,
                  padding: '0.125rem 0.5rem', borderRadius: '999px',
                  background: activity.projectRequest.status?.color ? activity.projectRequest.status.color + '20' : '#e0e7ff',
                  color: activity.projectRequest.status?.color ?? '#4338ca',
                }}>
                  📋 {activity.projectRequest.title}
                </span>
              )}
              <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)' }}>
                {formatDate(activity.createdAt)}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.375rem' }}>
            {canDelete && (
              <button
                onClick={() => {
                  if (window.confirm(`Aktivität "${activity.subject}" löschen?`)) onDelete();
                }}
                style={{
                  padding: '0.25rem 0.5rem', fontSize: '0.75rem', fontWeight: 600,
                  background: 'var(--color-error)', color: 'white',
                  border: 'none', borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                }}
              >
                Löschen
              </button>
            )}
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.25rem', color: 'var(--color-text-muted)', padding: '0.25rem' }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Beschreibung */}
        {activity.description && (
          <div style={{ marginBottom: '1rem' }}>
            <ActivityDescription description={activity.description} type={activity.type} />
          </div>
        )}

        {activity.scheduledAt && (
          <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginBottom: '0.25rem' }}>
            Geplant: {formatDate(activity.scheduledAt)}
          </div>
        )}
        {activity.completedAt && (
          <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginBottom: '0.5rem' }}>
            Abgeschlossen: {formatDate(activity.completedAt)}
          </div>
        )}

        {/* Projektanfrage-Zuordnung */}
        {projectRequests.length > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            marginBottom: '1rem', padding: '0.625rem 0.75rem',
            background: 'var(--color-bg)', borderRadius: 'var(--radius-sm)',
            border: '1px solid var(--color-border)',
          }}>
            <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--color-text)', whiteSpace: 'nowrap' }}>
              Projektanfrage:
            </span>
            <select
              value={activity.projectRequestId ?? ''}
              onChange={(e) => {
                const val = e.target.value || null;
                updateActivity.mutate(
                  { id: activity.id, data: { projectRequestId: val } as any },
                  {
                    onSuccess: () => {
                      qc.invalidateQueries({ queryKey: ['crm', 'activity', activity.id] });
                      qc.invalidateQueries({ queryKey: ['crm', 'contacts'] });
                    },
                  },
                );
              }}
              style={{
                flex: 1, padding: '0.375rem 0.5rem',
                border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)',
                fontSize: '0.8125rem', background: 'var(--color-bg-card)', color: 'var(--color-text)',
                outline: 'none', cursor: 'pointer',
              }}
            >
              <option value="">— Keine Zuordnung —</option>
              {projectRequests.map((pr) => (
                <option key={pr.id} value={pr.id}>
                  {pr.title}{pr.status ? ` (${pr.status.name})` : ''}
                </option>
              ))}
            </select>
            {updateActivity.isPending && (
              <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>...</span>
            )}
          </div>
        )}

        {/* Kommentare */}
        <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: '1rem', marginTop: '0.5rem' }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: 600, margin: '0 0 0.75rem' }}>
            Kommentare {comments.length > 0 && `(${comments.length})`}
          </h3>

          {/* Neuer Kommentar */}
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
            <textarea
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              placeholder="Kommentar hinzufügen..."
              style={{
                flex: 1, padding: '0.5rem 0.75rem',
                border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)',
                fontSize: '0.8125rem', resize: 'vertical', minHeight: 40,
                background: 'var(--color-bg-card)', color: 'var(--color-text)',
                boxSizing: 'border-box', outline: 'none',
              }}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
            />
            <button
              type="button"
              onClick={() => handleAddComment()}
              disabled={!newComment.trim() || saving}
              style={{
                padding: '0.5rem 0.75rem', fontSize: '0.8125rem', fontWeight: 600,
                background: 'var(--color-primary)', color: 'white',
                border: 'none', borderRadius: 'var(--radius-sm)',
                cursor: !newComment.trim() || saving ? 'default' : 'pointer',
                opacity: !newComment.trim() || saving ? 0.5 : 1,
                alignSelf: 'flex-end', whiteSpace: 'nowrap',
              }}
            >
              {saving ? '...' : 'Senden'}
            </button>
          </div>

          {commentError && (
            <p style={{ fontSize: '0.75rem', color: 'var(--color-error)', margin: '0.25rem 0' }}>{commentError}</p>
          )}

          {/* Kommentarliste */}
          {comments.length === 0 ? (
            <p style={{ fontSize: '0.8125rem', color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
              Noch keine Kommentare
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {comments.map((c) => (
                <div
                  key={c.id}
                  style={{
                    padding: '0.625rem 0.75rem',
                    background: 'var(--color-bg)',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-sm)',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                    <span style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--color-text)' }}>
                      {c.userName}
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>
                        {new Date(c.createdAt).toLocaleString('de-DE', {
                          day: '2-digit', month: '2-digit', year: 'numeric',
                          hour: '2-digit', minute: '2-digit',
                        })}
                      </span>
                      {canDelete && (
                        <button
                          onClick={() => handleDeleteComment(c.id)}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            color: 'var(--color-text-muted)', padding: '0', fontSize: '0.75rem',
                          }}
                          title="Kommentar löschen"
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', whiteSpace: 'pre-wrap' }}>
                    {c.text}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ActivityTimelineItem({
  activity: act,
  canDelete,
  onDelete,
  onOpenDetail,
  projectRequests,
}: {
  activity: Activity;
  canDelete: boolean;
  onDelete: () => void;
  onOpenDetail: () => void;
  projectRequests: CompanyProjectRequest[];
}) {
  const [expanded, setExpanded] = useState(false);
  const qc = useQueryClient();
  const updateActivity = useUpdateActivity();

  const iconClass = {
    NOTE: styles.timelineIconNote,
    CALL: styles.timelineIconCall,
    EMAIL: styles.timelineIconEmail,
    MEETING: styles.timelineIconMeeting,
    TASK: styles.timelineIconTask,
    FOLLOWUP: styles.timelineIconFollowup,
    APPOINTMENT: styles.timelineIconAppointment,
    SOCIAL_MEDIA: styles.timelineIconSocialMedia,
    MESSENGER: styles.timelineIconMessenger,
    EVENT: styles.timelineIconEvent,
  }[act.type] ?? '';

  const badgeClass = {
    NOTE: styles.timelineBadgeNote,
    CALL: styles.timelineBadgeCall,
    EMAIL: styles.timelineBadgeEmail,
    MEETING: styles.timelineBadgeMeeting,
    TASK: styles.timelineBadgeTask,
    FOLLOWUP: styles.timelineBadgeFollowup,
    APPOINTMENT: styles.timelineBadgeAppointment,
    SOCIAL_MEDIA: styles.timelineBadgeSocialMedia,
    MESSENGER: styles.timelineBadgeMessenger,
    EVENT: styles.timelineBadgeEvent,
  }[act.type] ?? '';

  return (
    <div className={styles.timelineItem} style={{ cursor: 'pointer' }}>
      <div className={`${styles.timelineIcon} ${iconClass}`}>
        {activityIcon(act.type)}
      </div>
      <div className={styles.timelineContent} style={{ flex: 1 }}>
        {/* Collapsed header — immer sichtbar */}
        <div
          onClick={() => setExpanded((p) => !p)}
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
              <svg
                width="10" height="10" viewBox="0 0 10 10"
                style={{
                  transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
                  transition: 'transform 0.15s',
                  flexShrink: 0,
                  color: 'var(--color-text-muted)',
                }}
              >
                <path d="M3 1l5 4-5 4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div className={styles.timelineSubject}>{act.subject}</div>
            </div>
            <div className={styles.timelineMeta} style={{ marginLeft: '1.125rem' }}>
              <span className={`${styles.timelineTypeBadge} ${badgeClass}`}>
                {ACTIVITY_TYPE_LABELS[act.type]}
              </span>
              {act.projectRequest && (
                <span style={{
                  fontSize: '0.6875rem', fontWeight: 600,
                  padding: '0.0625rem 0.375rem', borderRadius: '999px',
                  background: act.projectRequest.status?.color ? act.projectRequest.status.color + '20' : '#e0e7ff',
                  color: act.projectRequest.status?.color ?? '#4338ca',
                }}>
                  {act.projectRequest.title}
                </span>
              )}
              <span className={styles.timelineDate}>
                {formatDate(act.scheduledAt ?? act.createdAt)}
              </span>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '0.25rem', flexShrink: 0, alignItems: 'center' }}>
            {canDelete && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (window.confirm(`Aktivität "${act.subject}" löschen?`)) onDelete();
                }}
                title="Löschen"
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--color-text-muted)', padding: '0.125rem',
                  opacity: 0.5, transition: 'opacity 0.15s',
                }}
                onMouseOver={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.color = 'var(--color-error)'; }}
                onMouseOut={(e) => { e.currentTarget.style.opacity = '0.5'; e.currentTarget.style.color = 'var(--color-text-muted)'; }}
              >
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                  <path d="M3 3l6 6M9 3l-6 6" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Expanded content */}
        {expanded && (
          <div style={{ marginTop: '0.5rem', marginLeft: '1.125rem' }}>
            {/* Beschreibung */}
            {act.description && (
              <div style={{
                padding: '0.625rem 0.75rem', background: 'var(--color-bg)', borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--color-border)', fontSize: '0.8125rem',
                color: 'var(--color-text-secondary)', marginBottom: '0.5rem',
                maxHeight: 200, overflow: 'auto', lineHeight: 1.5,
              }}>
                <ActivityDescription description={act.description} type={act.type} />
              </div>
            )}

            {/* Zeitstempel */}
            <div style={{ display: 'flex', gap: '1rem', fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: '0.5rem' }}>
              {act.scheduledAt && <span>Geplant: {formatDate(act.scheduledAt)}</span>}
              {act.completedAt && <span>Abgeschlossen: {formatDate(act.completedAt)}</span>}
            </div>

            {/* Projektanfrage-Zuordnung */}
            {projectRequests.length > 0 && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: '0.5rem',
                marginBottom: '0.5rem', padding: '0.5rem 0.625rem',
                background: 'var(--color-bg)', borderRadius: 'var(--radius-sm)',
                border: '1px solid var(--color-border)',
              }}>
                <span style={{ fontSize: '0.75rem', fontWeight: 500, color: 'var(--color-text)', whiteSpace: 'nowrap' }}>
                  Projektanfrage:
                </span>
                <select
                  value={act.projectRequestId ?? ''}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => {
                    const val = e.target.value || null;
                    updateActivity.mutate(
                      { id: act.id, data: { projectRequestId: val } as any },
                      {
                        onSuccess: () => {
                          qc.invalidateQueries({ queryKey: ['crm', 'contacts'] });
                        },
                      },
                    );
                  }}
                  style={{
                    flex: 1, padding: '0.25rem 0.375rem',
                    border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)',
                    fontSize: '0.75rem', background: 'var(--color-bg-card)', color: 'var(--color-text)',
                    outline: 'none', cursor: 'pointer',
                  }}
                >
                  <option value="">— Keine Zuordnung —</option>
                  {projectRequests.map((pr) => (
                    <option key={pr.id} value={pr.id}>
                      {pr.title}{pr.status ? ` (${pr.status.name})` : ''}
                    </option>
                  ))}
                </select>
                {updateActivity.isPending && (
                  <span style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>...</span>
                )}
              </div>
            )}

            {/* Detail-Button */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onOpenDetail(); }}
              style={{
                padding: '0.25rem 0.625rem', fontSize: '0.75rem', fontWeight: 500,
                background: 'transparent', border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                color: 'var(--color-text-secondary)',
              }}
            >
              Kommentare anzeigen
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useContact(id!);
  const { data: dealsData } = useDeals({ contactId: id, pageSize: 50 });
  const deleteMutation = useDeleteContact();
  const deleteActivityMut = useDeleteActivity();
  const { user: currentUser } = useAuth();
  const { hasPermission } = usePermissions();
  const canDeleteActivities = currentUser?.role === 'PLATFORM_ADMIN' || hasPermission('crm.admin');

  const [pageTab, setPageTab] = useState<PageTab>('details');
  const [isEditOpen, setEditOpen] = useState(false);
  const [isActivityOpen, setActivityOpen] = useState(false);
  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(null);
  const [m365Tab, setM365Tab] = useState<M365Tab>('emails');
  const [pushStatus, setPushStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  // Projektanfragen des zugehoerigen Unternehmens laden (fuer Zuordnung)
  const companyId = data?.data?.companyId ?? data?.data?.company?.id ?? '';
  const { data: prData } = useProjectRequests(companyId);
  const companyProjectRequests: CompanyProjectRequest[] = prData?.data ?? [];

  // Tenant-User-Liste für Owner-Name-Auflösung
  const { data: usersData } = useTenantUsers();
  const tenantUsers: TenantUser[] = usersData?.data ?? [];

  // Aktivitäten-Filter
  const [actTypeFilter, setActTypeFilter] = useState<ActivityType | 'ALL'>('ALL');
  const [actDateFrom, setActDateFrom] = useState('');
  const [actDateTo, setActDateTo] = useState('');
  const [actSearch, setActSearch] = useState('');

  // Timeline-Gruppen (Jahr / Monat)
  const [expandedYears, setExpandedYears] = useState<Set<number>>(new Set());
  const [expandedMonths, setExpandedMonths] = useState<Set<string>>(new Set());

  // Initiale Collapse-Logik: Monate > 6 Monate → zugeklappt, Jahre > 12 Monate → zugeklappt
  const contactDataId = data?.data?.id;
  useEffect(() => {
    const acts: Activity[] = data?.data?.activities ?? [];
    if (!acts.length) return;
    const now = new Date();
    const sixMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 6, 1);
    const twelveMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 12, 1);
    const openMonths = new Set<string>();
    const openYears = new Set<number>();
    acts.forEach((act) => {
      const d = new Date(act.scheduledAt ?? act.createdAt);
      const monthStart = new Date(d.getFullYear(), d.getMonth(), 1);
      const nextYearStart = new Date(d.getFullYear() + 1, 0, 1);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (monthStart >= sixMonthsAgo) openMonths.add(monthKey);
      if (nextYearStart >= twelveMonthsAgo) openYears.add(d.getFullYear());
    });
    setExpandedMonths(openMonths);
    setExpandedYears(openYears);
  }, [contactDataId]);

  if (isLoading) return <p>Laden…</p>;
  if (error || !data)
    return (
      <p style={{ color: 'var(--color-error)' }}>
        Kontakt konnte nicht geladen werden
      </p>
    );

  const contact = data.data;
  const allActivities: Activity[] = contact.activities ?? [];
  const deals = dealsData?.data ?? [];

  // Account Owner Name auflösen
  const ownerUserId = contact.owners?.[0]?.userId;
  const ownerUser = tenantUsers.find((u) => u.id === ownerUserId);
  const ownerName = ownerUser
    ? [ownerUser.firstName, ownerUser.lastName].filter(Boolean).join(' ') || ownerUser.email
    : ownerUserId ?? null;

  // Letzter Kontakt = neueste Aktivität
  const _latestAct = allActivities.length
    ? [...allActivities].sort((a, b) =>
        new Date(b.scheduledAt ?? b.createdAt).getTime() - new Date(a.scheduledAt ?? a.createdAt).getTime()
      )[0]
    : null;
  const lastContactDate: Date | null = _latestAct
    ? new Date(_latestAct.scheduledAt ?? _latestAct.createdAt)
    : null;

  // Gefilterte Aktivitäten
  const activities = allActivities.filter((act) => {
    if (actTypeFilter !== 'ALL' && act.type !== actTypeFilter) return false;
    if (actDateFrom) {
      const actDate = new Date(act.scheduledAt ?? act.createdAt).toISOString().slice(0, 10);
      if (actDate < actDateFrom) return false;
    }
    if (actDateTo) {
      const actDate = new Date(act.scheduledAt ?? act.createdAt).toISOString().slice(0, 10);
      if (actDate > actDateTo) return false;
    }
    if (actSearch.trim()) {
      const q = actSearch.trim().toLowerCase();
      if (
        !(act.subject?.toLowerCase().includes(q)) &&
        !(act.description?.toLowerCase().includes(q))
      ) return false;
    }
    return true;
  });

  const isFilterActive = actTypeFilter !== 'ALL' || actDateFrom !== '' || actDateTo !== '' || actSearch.trim() !== '';

  const MONTH_NAMES_DE = ['Januar','Februar','März','April','Mai','Juni','Juli','August','September','Oktober','November','Dezember'];

  const toggleYear = (year: number) =>
    setExpandedYears((prev) => { const next = new Set(prev); if (next.has(year)) next.delete(year); else next.add(year); return next; });

  const toggleMonth = (key: string) =>
    setExpandedMonths((prev) => { const next = new Set(prev); if (next.has(key)) next.delete(key); else next.add(key); return next; });

  // Aktivitäten nach Datum sortieren und nach Jahr/Monat gruppieren
  const sortedActs = [...activities].sort((a, b) =>
    new Date(b.scheduledAt ?? b.createdAt).getTime() - new Date(a.scheduledAt ?? a.createdAt).getTime()
  );
  const yearMap = new Map<number, Map<string, Activity[]>>();
  for (const act of sortedActs) {
    const d = new Date(act.scheduledAt ?? act.createdAt);
    const yr = d.getFullYear();
    const mk = `${yr}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (!yearMap.has(yr)) yearMap.set(yr, new Map());
    const mm = yearMap.get(yr)!;
    if (!mm.has(mk)) mm.set(mk, []);
    mm.get(mk)!.push(act);
  }
  const groupedTimeline = [...yearMap.entries()]
    .sort(([a], [b]) => b - a)
    .map(([year, mm]) => ({
      year,
      months: [...mm.entries()]
        .sort(([a], [b]) => b.localeCompare(a))
        .map(([monthKey, acts]) => ({
          monthKey,
          label: MONTH_NAMES_DE[Number(monthKey.split('-')[1]) - 1],
          acts,
        })),
    }));

  function resetFilters() {
    setActTypeFilter('ALL');
    setActDateFrom('');
    setActDateTo('');
    setActSearch('');
  }

  async function handlePushToOutlook() {
    setPushStatus('loading');
    try {
      await contactsApi.pushToOutlook(contact.id);
      setPushStatus('success');
      setTimeout(() => setPushStatus('idle'), 3000);
    } catch {
      setPushStatus('error');
      setTimeout(() => setPushStatus('idle'), 3000);
    }
  }

  return (
    <div style={{ maxWidth: '960px', margin: '0 auto' }}>
      {/* Breadcrumb */}
      <nav style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.8125rem', color: 'var(--color-text-muted)', marginBottom: '1rem' }}>
        <Link to="/crm?tab=contacts" style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>CRM</Link>
        <span style={{ fontSize: '0.6875rem' }}>›</span>
        <Link to="/crm?tab=contacts" style={{ color: 'var(--color-primary)', textDecoration: 'none' }}>Kontakte</Link>
        <span style={{ fontSize: '0.6875rem' }}>›</span>
        <span style={{ color: 'var(--color-text-secondary)', fontWeight: 500 }}>{contactDisplayName(contact)}</span>
      </nav>

      {/* ── Header ── */}
      {(() => {
        const headerName = contactDisplayName(contact);
        const headerInitials = contact.type === 'ORGANIZATION'
          ? (contact.companyName?.[0] ?? '?').toUpperCase()
          : ([contact.firstName?.[0], contact.lastName?.[0]].filter(Boolean).join('').toUpperCase() || '?');
        const avatarPalette = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#6366f1', '#ef4444', '#14b8a6'];
        const headerAvatarColor = avatarPalette[(headerName.charCodeAt(0) + (headerName.charCodeAt(1) || 0)) % avatarPalette.length];
        return (
          <div className={styles.header}>
            <div className={styles.headerLeft}>
              {/* Avatar */}
              <div style={{
                width: 72, height: 72, borderRadius: '50%',
                background: headerAvatarColor, color: 'white', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '1.5rem', fontWeight: 700,
              }}>
                {headerInitials}
              </div>
              <div>
                <div className={styles.nameRow}>
                  <h1 className={styles.name}>
                    {headerName}
                    {contact.company && (
                      <span className={styles.nameCompany}>
                        {' '}({contact.company.name})
                      </span>
                    )}
                  </h1>
                  {contact.company?.businessEntity && (
                    <span style={{
                      fontSize: '0.6875rem', fontWeight: 700,
                      padding: '0.125rem 0.5rem', borderRadius: '999px',
                      color: 'white', background: '#6366f1',
                      marginLeft: '0.375rem',
                    }}>
                      {contact.company.businessEntity.shortName ?? contact.company.businessEntity.name}
                    </span>
                  )}
                  <span className={contact.isActive ? styles.badgeActive : styles.badgeInactive}>
                    {contact.isActive ? 'Aktiv' : 'Inaktiv'}
                  </span>
                </div>
                {contact.position && (
                  <p className={styles.subtitle}>{contact.position}</p>
                )}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, alignItems: 'center' }}>
              {pushStatus === 'success' && (
                <span style={{ fontSize: '0.8125rem', color: '#16a34a' }}>✓ Gespeichert</span>
              )}
              {pushStatus === 'error' && (
                <span style={{ fontSize: '0.8125rem', color: 'var(--color-error)' }}>Fehler</span>
              )}
              {contact.email && (
                <button
                  onClick={handlePushToOutlook}
                  disabled={pushStatus === 'loading'}
                  title="Kontakt in Outlook-Kontakte speichern"
                  style={{
                    display: 'flex', alignItems: 'center', gap: '0.375rem',
                    padding: '0.375rem 0.75rem',
                    fontSize: '0.8125rem', fontWeight: 500,
                    background: 'transparent',
                    border: '1px solid var(--color-border)',
                    borderRadius: 'var(--radius-sm)',
                    cursor: pushStatus === 'loading' ? 'wait' : 'pointer',
                    color: 'var(--color-text-secondary)',
                    opacity: pushStatus === 'loading' ? 0.6 : 1,
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="12" height="12" rx="2" />
                    <path d="M5 5h2.5v6H5z" fill="currentColor" stroke="none" opacity="0.3" />
                    <path d="M5 5l4 3-4 3z" fill="currentColor" stroke="none" opacity="0.3" />
                  </svg>
                  {pushStatus === 'loading' ? 'Speichern...' : 'In Outlook Kontakte'}
                </button>
              )}
              <button
                onClick={() => setEditOpen(true)}
                style={{
                  padding: '0.375rem 0.875rem',
                  fontSize: '0.8125rem', fontWeight: 600,
                  background: 'var(--color-primary)',
                  border: 'none',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  color: 'white',
                }}
              >
                Bearbeiten
              </button>
              <button
                onClick={() => setDeleteOpen(true)}
                title="Kontakt löschen"
                style={{
                  padding: '0.375rem 0.625rem',
                  fontSize: '0.8125rem',
                  background: 'transparent',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-sm)',
                  cursor: 'pointer',
                  color: 'var(--color-text-muted)',
                  transition: 'all 0.15s',
                }}
                onMouseOver={(e) => { e.currentTarget.style.color = 'var(--color-error)'; e.currentTarget.style.borderColor = '#fecaca'; }}
                onMouseOut={(e) => { e.currentTarget.style.color = 'var(--color-text-muted)'; e.currentTarget.style.borderColor = 'var(--color-border)'; }}
              >
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 4h12M5.333 4V2.667a1.333 1.333 0 011.334-1.334h2.666a1.333 1.333 0 011.334 1.334V4m2 0v9.333a1.333 1.333 0 01-1.334 1.334H4.667a1.333 1.333 0 01-1.334-1.334V4h9.334z" />
                </svg>
              </button>
            </div>
          </div>
        );
      })()}

      {/* ── Page Tabs ── */}
      <div style={{
        display: 'flex', gap: 0, borderBottom: '2px solid var(--color-border)',
        marginBottom: '1.5rem', marginTop: '0.5rem',
      }}>
        {([
          { key: 'details' as PageTab, label: 'Kontakt Details' },
          { key: 'activities' as PageTab, label: 'Kontakthistorie' },
          ...(contact.email ? [{ key: 'outlook' as PageTab, label: 'Outlook Daten' }] : []),
        ]).map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setPageTab(t.key)}
            style={{
              padding: '0.625rem 1.25rem',
              fontSize: '0.9375rem',
              fontWeight: pageTab === t.key ? 600 : 500,
              color: pageTab === t.key ? 'var(--color-primary)' : 'var(--color-text-muted)',
              background: 'none',
              border: 'none',
              borderBottom: pageTab === t.key ? '2px solid var(--color-primary)' : '2px solid transparent',
              marginBottom: '-2px',
              cursor: 'pointer',
              transition: 'all 0.15s',
            }}
          >
            {t.label}
            {t.key === 'activities' && allActivities.length > 0 && (
              <span style={{ marginLeft: '0.375rem', fontSize: '0.8125rem', fontWeight: 400, color: 'var(--color-text-muted)' }}>
                ({allActivities.length})
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ── Tab: Kontakt Details ── */}
      {pageTab === 'details' && (
      <div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem', alignItems: 'start' }}>
          {/* ── Card 1: Kontaktdaten ── */}
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Kontaktdaten</h2>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}><IconEmail /> E-Mail</span>
                <div className={styles.infoValue}>
                  {contact.email ? (
                    <CopyField value={contact.email} id="email">
                      <a href={`mailto:${contact.email}`} style={{ color: 'var(--color-primary)' }}>
                        {contact.email}
                      </a>
                    </CopyField>
                  ) : <span className={styles.empty}>—</span>}
                </div>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}><IconPhone /> Telefon</span>
                <div className={styles.infoValue}>
                  {contact.phone ? (
                    <CopyField value={contact.phone} id="phone">
                      <a href={`tel:${contact.phone}`} style={{ color: 'var(--color-text)' }}>{contact.phone}</a>
                    </CopyField>
                  ) : <span className={styles.empty}>—</span>}
                </div>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}><IconPhone /> Mobil</span>
                <div className={styles.infoValue}>
                  {contact.mobile ? (
                    <CopyField value={contact.mobile} id="mobile">
                      <a href={`tel:${contact.mobile}`} style={{ color: 'var(--color-text)' }}>{contact.mobile}</a>
                    </CopyField>
                  ) : <span className={styles.empty}>—</span>}
                </div>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}><LinkedInIcon /> LinkedIn</span>
                <div className={styles.infoValue}>
                  {contact.linkedinUrl ? (
                    <a href={contact.linkedinUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)' }}>
                      {contact.linkedinUrl.replace(/^https?:\/\/(www\.)?linkedin\.com\/in\//, '')}
                    </a>
                  ) : <span className={styles.empty}>—</span>}
                </div>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}><XingIcon /> Xing</span>
                <div className={styles.infoValue}>
                  {contact.xingUrl ? (
                    <a href={contact.xingUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)' }}>
                      {contact.xingUrl.replace(/^https?:\/\/(www\.)?xing\.com\/profile\//, '')}
                    </a>
                  ) : <span className={styles.empty}>—</span>}
                </div>
              </div>
              {contact.website && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}><IconGlobe /> Website</span>
                  <div className={styles.infoValue}>
                    <a href={/^https?:\/\//i.test(contact.website) ? contact.website : `https://${contact.website}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--color-primary)' }}>
                      {contact.website}
                    </a>
                  </div>
                </div>
              )}
              {(contact.street || contact.zip || contact.city || contact.state) && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}><IconPin /> Adresse</span>
                  <div className={styles.infoValue}>
                    {contact.street && <>{contact.street}<br /></>}
                    {contact.zip} {contact.city}
                    {contact.state && <>, {contact.state}</>}
                    {contact.country && contact.country !== 'DE' && <><br />{contact.country}</>}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Card 2: Berufliches ── */}
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>Berufliches</h2>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Unternehmen</span>
                <div className={styles.infoValue}>
                  {contact.company ? (
                    <>
                      <Link to={`/crm/companies/${contact.company.id}`} style={{ color: 'var(--color-primary)' }}>
                        {contact.company.name}
                      </Link>
                      {contact.company.industry && (
                        <span style={{ color: 'var(--color-text-muted)', marginLeft: '0.5rem', fontSize: '0.8125rem' }}>
                          ({contact.company.industry})
                        </span>
                      )}
                    </>
                  ) : contact.companyName ? (
                    <span>{contact.companyName}</span>
                  ) : <span className={styles.empty}>—</span>}
                </div>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Position</span>
                <div className={styles.infoValue}>{contact.position ?? <span className={styles.empty}>—</span>}</div>
              </div>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Abteilung</span>
                <div className={styles.infoValue}>{contact.department ?? <span className={styles.empty}>—</span>}</div>
              </div>
              {contact.preferredContact && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Kontaktweg</span>
                  <div className={styles.infoValue}>{PREFERRED_CONTACT_LABELS[contact.preferredContact] ?? contact.preferredContact}</div>
                </div>
              )}
              {contact.decisionLevel && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Entscheider-Level</span>
                  <div className={styles.infoValue}>{DECISION_LEVEL_LABELS[contact.decisionLevel] ?? contact.decisionLevel}</div>
                </div>
              )}
              {contact.preferredLanguage && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Sprache</span>
                  <div className={styles.infoValue}>{contact.preferredLanguage}</div>
                </div>
              )}
              {contact.timezone && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Zeitzone</span>
                  <div className={styles.infoValue}>{contact.timezone}</div>
                </div>
              )}
              {contact.nextStep && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Nächster Schritt</span>
                  <div className={styles.infoValue} style={{ whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>
                    {contact.nextStep}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ── Card 3: System-Details ── */}
          <div className={styles.card}>
            <h2 className={styles.cardTitle}>System-Details</h2>
            <div className={styles.infoGrid}>
              <div className={styles.infoItem}>
                <span className={styles.infoLabel}>Status</span>
                <div className={styles.infoValue}
                  style={{
                    color: contact.status === 'BLOCKED'
                      ? 'var(--color-error-text)'
                      : contact.status === 'INACTIVE'
                      ? 'var(--color-text-muted)'
                      : 'var(--color-text)',
                  }}
                >
                  {ENTITY_STATUS_LABELS[contact.status] ?? contact.status}
                </div>
              </div>
              {ownerName && (() => {
                const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}/i.test(ownerName);
                return (
                  <div className={styles.infoItem}>
                    <span className={styles.infoLabel}>Account Owner</span>
                    <div className={styles.infoValue}>
                      {isUuid ? <span className={styles.empty} title={ownerName}>Unbekannt</span> : ownerName}
                    </div>
                  </div>
                );
              })()}
              {lastContactDate && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Letzter Kontakt</span>
                  <div className={styles.infoValue}>
                    {lastContactDate.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                  </div>
                </div>
              )}
              {contact.birthday && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Geburtsdatum</span>
                  <div className={styles.infoValue}>{new Date(contact.birthday).toLocaleDateString('de-DE')}</div>
                </div>
              )}
              {contact.source && (
                <div className={styles.infoItem}>
                  <span className={styles.infoLabel}>Quelle</span>
                  <div className={styles.infoValue}>{contact.source}</div>
                </div>
              )}
            </div>
          </div>
        </div>{/* end 3-column grid */}

        {/* Notizen & Tags card */}
          {((contact.tags && contact.tags.length > 0) ||
            contact.notes ||
            (contact.customFields && contact.customFields.length > 0)) && (
            <div className={styles.card} style={{ marginTop: '1.5rem' }}>
              <h2 className={styles.cardTitle}>Notizen &amp; Tags</h2>

              {contact.tags && contact.tags.length > 0 && (
                <div
                  style={{
                    marginBottom: contact.notes ? '1rem' : 0,
                  }}
                >
                  <div className={styles.tags}>
                    {contact.tags.map((tag) => (
                      <span key={tag} className={styles.tag}>
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {contact.notes && (
                <p
                  className={styles.notesText}
                  style={{ marginTop: contact.tags?.length ? '0.75rem' : 0 }}
                >
                  {contact.notes}
                </p>
              )}

              {contact.customFields && contact.customFields.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                  <CustomFieldsDisplay fields={contact.customFields} />
                </div>
              )}
            </div>
          )}

          {/* Linked deals */}
          {deals.length > 0 && (
            <div className={styles.card} style={{ marginTop: '1.5rem' }}>
              <h2 className={styles.cardTitle}>
                Verknüpfte Vorgänge ({deals.length})
              </h2>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                    <th
                      style={{
                        padding: '0.5rem 0',
                        textAlign: 'left',
                        fontSize: '0.75rem',
                        color: 'var(--color-text-muted)',
                        textTransform: 'uppercase',
                      }}
                    >
                      Titel
                    </th>
                    <th
                      style={{
                        padding: '0.5rem 0',
                        textAlign: 'left',
                        fontSize: '0.75rem',
                        color: 'var(--color-text-muted)',
                        textTransform: 'uppercase',
                      }}
                    >
                      Stage
                    </th>
                    <th
                      style={{
                        padding: '0.5rem 0',
                        textAlign: 'right',
                        fontSize: '0.75rem',
                        color: 'var(--color-text-muted)',
                        textTransform: 'uppercase',
                      }}
                    >
                      Wert
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {deals.map((deal) => (
                    <tr
                      key={deal.id}
                      style={{
                        borderBottom: '1px solid var(--color-border)',
                        cursor: 'pointer',
                      }}
                      onClick={() => navigate(`/crm/deals/${deal.id}`)}
                    >
                      <td
                        style={{ padding: '0.5rem 0', fontSize: '0.875rem' }}
                      >
                        {deal.title}
                      </td>
                      <td style={{ padding: '0.5rem 0' }}>
                        {deal.stage && (
                          <span
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '0.375rem',
                              fontSize: '0.8125rem',
                            }}
                          >
                            <span
                              style={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                background: deal.stage.color,
                                display: 'inline-block',
                              }}
                            />
                            {deal.stage.name}
                          </span>
                        )}
                      </td>
                      <td
                        style={{
                          padding: '0.5rem 0',
                          textAlign: 'right',
                          fontSize: '0.875rem',
                          fontWeight: 500,
                        }}
                      >
                        {deal.value
                          ? currencyFormatter.format(parseFloat(deal.value))
                          : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

      </div>

      )}

      {/* ── Tab: Aktivitäten ── */}
      {pageTab === 'activities' && (
      <div>
      <div className={styles.card}>
        {/* Karten-Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '1rem',
          }}
        >
          <h2 className={styles.cardTitle} style={{ margin: 0 }}>
            Kontakthistorie
            {allActivities.length > 0 && (
              <span
                style={{
                  marginLeft: '0.5rem',
                  fontSize: '0.8125rem',
                  fontWeight: 400,
                  color: 'var(--color-text-muted)',
                }}
              >
                ({allActivities.length})
              </span>
            )}
          </h2>
          <button
            onClick={() => setActivityOpen(true)}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.375rem',
              padding: '0.5rem 1rem',
              fontSize: '0.8125rem',
              background: 'var(--color-primary)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              cursor: 'pointer',
              fontWeight: 600,
            }}
          >
            + Neue Aktivität
          </button>
        </div>

        {/* Suche + Filter-Leiste */}
        {allActivities.length > 0 && (
          <div className={styles.activityFilterBar}>
            <input
              type="search"
              placeholder="Aktivitäten durchsuchen..."
              value={actSearch}
              onChange={(e) => setActSearch(e.target.value)}
              style={{
                padding: '0.375rem 0.625rem',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-sm)',
                fontSize: '0.8125rem',
                background: 'var(--color-bg-card)',
                color: 'var(--color-text)',
                outline: 'none',
                minWidth: 180,
              }}
            />
            <span className={styles.activityFilterLabel}>Typ:</span>
            <select
              className={styles.activityFilterSelect}
              value={actTypeFilter}
              onChange={(e) =>
                setActTypeFilter(e.target.value as ActivityType | 'ALL')
              }
            >
              <option value="ALL">Alle</option>
              {(Object.keys(ACTIVITY_TYPE_LABELS) as ActivityType[]).map(
                (t) => (
                  <option key={t} value={t}>
                    {ACTIVITY_TYPE_LABELS[t]}
                  </option>
                ),
              )}
            </select>

            <span className={styles.activityFilterLabel}>Von:</span>
            <input
              type="date"
              className={styles.activityFilterDate}
              value={actDateFrom}
              onChange={(e) => setActDateFrom(e.target.value)}
            />

            <span className={styles.activityFilterLabel}>Bis:</span>
            <input
              type="date"
              className={styles.activityFilterDate}
              value={actDateTo}
              onChange={(e) => setActDateTo(e.target.value)}
            />

            {isFilterActive && (
              <button
                type="button"
                className={styles.activityFilterReset}
                onClick={resetFilters}
              >
                × Filter zurücksetzen
              </button>
            )}

            <span className={styles.activityFilterCount}>
              {activities.length} von {allActivities.length}
            </span>
          </div>
        )}

        {/* Zeitstrahl */}
        {activities.length === 0 ? (
          <p className={styles.timelineEmpty}>
            {isFilterActive
              ? 'Keine Aktivitäten für den gewählten Filter'
              : 'Keine Aktivitäten vorhanden'}
          </p>
        ) : (
          <div className={styles.timeline}>
            {groupedTimeline.map(({ year, months }) => {
              const isYearOpen = expandedYears.has(year);
              const yearTotal = months.reduce((s, m) => s + m.acts.length, 0);
              return (
                <div key={year}>
                  {/* Jahres-Header */}
                  <button type="button" className={styles.timelineYearHeader} onClick={() => toggleYear(year)}>
                    <div className={styles.timelineYearDividerLine} />
                    <div className={styles.timelineYearPill}>
                      <svg width="9" height="9" viewBox="0 0 10 10"
                        style={{ transform: isYearOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', flexShrink: 0 }}
                      >
                        <path d="M3 1l5 4-5 4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      {year}
                      <span className={styles.timelineGroupCount}>{yearTotal}</span>
                    </div>
                    <div className={styles.timelineYearDividerLine} />
                  </button>

                  {isYearOpen && months.map(({ monthKey, label, acts }) => {
                    const isMonthOpen = expandedMonths.has(monthKey);
                    return (
                      <div key={monthKey}>
                        {/* Monats-Header */}
                        <button type="button" className={styles.timelineMonthHeader} onClick={() => toggleMonth(monthKey)}>
                          <div className={styles.timelineMonthDot}>
                            <svg width="8" height="8" viewBox="0 0 10 10"
                              style={{ transform: isMonthOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.15s', color: 'var(--color-text-muted)' }}
                            >
                              <path d="M3 1l5 4-5 4" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          </div>
                          <span className={styles.timelineMonthLabel}>{label}</span>
                          <span className={styles.timelineGroupCount}>{acts.length}</span>
                        </button>
                        {isMonthOpen && acts.map((act) => (
                          <ActivityTimelineItem
                            key={act.id}
                            activity={act}
                            canDelete={canDeleteActivities}
                            onDelete={() => deleteActivityMut.mutate(act.id)}
                            onOpenDetail={() => setSelectedActivity(act)}
                            projectRequests={companyProjectRequests}
                          />
                        ))}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        )}
      </div>

      </div>
      )}

      {/* ── Tab: Outlook Daten ── */}
      {pageTab === 'outlook' && contact.email && (
      <div>
        <div style={{ display: 'flex', gap: '0.5rem', borderBottom: '1px solid var(--color-border)', paddingBottom: 0, marginBottom: '1rem' }}>
          {(['emails', 'calendar'] as M365Tab[]).map((tab) => {
            const labels: Record<M365Tab, string> = { emails: 'E-Mails', calendar: 'Kalender' };
            return (
              <button
                key={tab}
                type="button"
                onClick={() => setM365Tab(tab)}
                style={{
                  padding: '0.5rem 1rem',
                  background: 'transparent',
                  border: 'none',
                  borderBottom: m365Tab === tab ? '2px solid var(--color-primary)' : '2px solid transparent',
                  fontSize: '0.875rem',
                  fontWeight: m365Tab === tab ? 600 : 400,
                  color: m365Tab === tab ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                  cursor: 'pointer',
                  marginBottom: '-1px',
                  transition: 'all 0.15s',
                }}
              >
                {labels[tab]}
              </button>
            );
          })}
        </div>

        <div className={styles.card}>
          {m365Tab === 'emails' && <EmailsTab contactId={contact.id} contactEmail={contact.email ?? undefined} />}
          {m365Tab === 'calendar' && <CalendarTab contactId={contact.id} />}
        </div>
      </div>
      )}

      {/* ── Aktivität Detail Popup ── */}
      {selectedActivity && (
        <ActivityDetailPopup
          activity={selectedActivity}
          onClose={() => setSelectedActivity(null)}
          canDelete={canDeleteActivities}
          onDelete={() => {
            deleteActivityMut.mutate(selectedActivity.id);
            setSelectedActivity(null);
          }}
          currentUser={currentUser}
          projectRequests={companyProjectRequests}
        />
      )}

      {/* ── Modals ── */}
      <ContactFormModal
        isOpen={isEditOpen}
        onClose={() => setEditOpen(false)}
        contact={contact}
        onSuccess={() => setEditOpen(false)}
      />

      <ActivityFormModal
        isOpen={isActivityOpen}
        onClose={() => setActivityOpen(false)}
        contactId={contact.id}
        onSuccess={() => setActivityOpen(false)}
      />

      {/* Delete confirmation */}
      <Modal
        isOpen={isDeleteOpen}
        onClose={() => setDeleteOpen(false)}
        title="Kontakt löschen"
        maxWidth="420px"
      >
        <p
          style={{
            fontSize: '0.9375rem',
            color: 'var(--color-text)',
            marginBottom: '0.5rem',
          }}
        >
          Soll der Kontakt{' '}
          <strong>{contactDisplayName(contact)}</strong> wirklich gelöscht
          werden?
        </p>
        <p
          style={{
            fontSize: '0.8125rem',
            color: 'var(--color-error)',
            marginBottom: '1.5rem',
          }}
        >
          Alle Aktivitäten werden ebenfalls gelöscht.
        </p>
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.75rem',
          }}
        >
          <button
            onClick={() => setDeleteOpen(false)}
            disabled={deleteMutation.isPending}
            style={{
              padding: '0.5rem 1rem',
              background: 'transparent',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.875rem',
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
            }}
          >
            Abbrechen
          </button>
          <button
            onClick={() =>
              deleteMutation.mutate(contact.id, {
                onSuccess: () => navigate('/crm?tab=contacts'),
              })
            }
            disabled={deleteMutation.isPending}
            style={{
              padding: '0.5rem 1rem',
              background: 'var(--color-error)',
              color: 'white',
              border: 'none',
              borderRadius: 'var(--radius-sm)',
              fontSize: '0.875rem',
              fontWeight: 600,
              cursor: deleteMutation.isPending ? 'wait' : 'pointer',
              opacity: deleteMutation.isPending ? 0.7 : 1,
            }}
          >
            {deleteMutation.isPending ? 'Löschen…' : 'Endgültig löschen'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
